/**
 * 아젠다 항목 클라이언트 정규화.
 *
 * 이 파일이 서버 필드명을 아는 **유일한 곳**이다. 계약이 바뀌어도 여기만 고친다.
 *
 * 기존 Calendar.vue 는 `{ start, end }` 와 `{ startDate, endDate }` 와
 * `{ extendedProps: {...} }` 를 "호환성을 위해" 셋 다 들고 다녔다(FullCalendar 잔재).
 * 그래서 하위 컴포넌트마다 `new Date(e.startDate || e.start)` 같은 방어 코드가 붙었다.
 * 여기서 한 번 정규화하고 그 형태만 쓴다.
 */

import { getEventTypeLabel } from './eventTypes'
import { getStatusLabel } from './taskStatus'

/** 'YYYY-MM-DD HH:mm:ss' → Date. Safari 는 공백 구분자를 파싱하지 못한다. */
export function parseServerDateTime(s) {
  if (!s) return null
  return new Date(String(s).replace(' ', 'T'))
}

/** Date → 'YYYY-MM-DD' (로컬 기준) */
export function ymd(date) {
  if (!date) return null
  if (typeof date === 'string') return date.slice(0, 10)
  const p = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`
}

/**
 * 서버 AgendaItem → 화면용 형태.
 * 서버가 이미 대부분 정규화해 주므로 여기서는 Date 객체와 표시용 라벨만 덧붙인다.
 */
function normalizeAgendaItem(raw) {
  const start = parseServerDateTime(raw.startAt)
  const end = parseServerDateTime(raw.endAt)

  const base = {
    uid: raw.uid,
    kind: raw.kind,
    id: raw.id,
    title: raw.title,
    description: raw.description,

    // 문자열(서버 계약) — 비교/버킷 키에 쓴다
    startAt: raw.startAt,
    endAt: raw.endAt,
    date: raw.date,
    endDate: raw.endDate,

    // Date 객체 — 렌더에 쓴다
    start,
    end,

    allDay: raw.allDay,
    spanDays: raw.spanDays,
    color: raw.color,
    owner: raw.owner,
    departmentId: raw.departmentId,
    department: raw.department,
    location: raw.location,
    canEdit: raw.canEdit,
    canDelete: raw.canDelete,
    linkPath: raw.linkPath
    // 서버 원본(raw)은 더 들고 다니지 않는다. 다이얼로그들이 정규화된 형태로 옮겨 온 뒤로
    // 읽는 곳이 없었고, 항목마다 원본 객체를 하나씩 더 붙잡고 있을 이유가 없다.
  }

  if (raw.kind === 'schedule') {
    return {
      ...base,
      eventType: raw.schedule?.eventType,
      locationCustom: raw.schedule?.locationCustom,
      // 반복 시리즈 소속 여부. 값이 있어야 '이 일정만 / 이후 전부 / 전체' 를 물어본다.
      // 단발이거나 시리즈에서 분리된 예외는 null 이고, 그때는 묻지 않고 바로 처리한다.
      seriesId: raw.schedule?.seriesId || null,
      typeLabel: getEventTypeLabel(raw.schedule?.eventType)
    }
  }

  return {
    ...base,
    status: raw.task?.status,
    statusLabel: getStatusLabel(raw.task?.status),
    priority: raw.task?.priority,
    category: raw.task?.category,
    isPrivate: raw.task?.isPrivate,
    creator: raw.task?.creator,
    creatorId: raw.task?.creatorId,
    assigneeId: raw.task?.assigneeId,
    supporterIds: raw.task?.supporterIds || [],
    startDate: raw.task?.startDate,
    dueDate: raw.task?.dueDate,
    startTime: raw.task?.startTime,
    dueTime: raw.task?.dueTime,
    completedAt: raw.task?.completedAt,
    unscheduled: raw.task?.unscheduled,
    isOverdue: raw.task?.isOverdue,
    canProgress: raw.task?.canProgress
  }
}

export function normalizeAgendaList(list) {
  return (list || []).map(normalizeAgendaItem)
}

/**
 * 항목이 특정 날짜를 덮는지.
 * 문자열 비교라 타임존 변환이 없다.
 */
function coversDate(item, dayYmd) {
  if (!item.date) return false
  return item.date <= dayYmd && (item.endDate || item.date) >= dayYmd
}

/** 하루치 항목만 추린다 */
export function itemsOnDate(items, dayYmd) {
  return items.filter((it) => coversDate(it, dayYmd))
}

/** 'HH:mm' 표시. 종일이면 빈 문자열. */
export function timeLabel(item) {
  if (item.allDay || !item.start) return ''
  const p = (n) => String(n).padStart(2, '0')
  return `${p(item.start.getHours())}:${p(item.start.getMinutes())}`
}

/** '09:00–18:00' 또는 '종일' */
export function timeRangeLabel(item) {
  if (item.allDay) return '종일'
  const p = (n) => String(n).padStart(2, '0')
  const f = (d) => `${p(d.getHours())}:${p(d.getMinutes())}`
  if (!item.start) return ''
  return item.end ? `${f(item.start)}–${f(item.end)}` : f(item.start)
}

/* ──────────────────────────── 날짜 이동 ────────────────────────────
 *
 * 월 그리드의 드래그 이동이 쓰는 순수 함수들.
 *
 * **전부 문자열 산술이다.** 항목이 들고 있는 date/endDate/startAt/endAt 은
 * 서버가 KST 고정폭으로 내려준 문자열이고, 그리드의 배치·정렬도 그 문자열
 * 비교로만 이뤄진다. 중간에 Date 로 왕복하면 (a) 브라우저 타임존이 KST 가
 * 아닌 사용자에게서 하루가 밀리고, (b) 종일 일정의 23:59:59 가 파싱→포맷
 * 과정에서 미묘하게 달라진다. 날짜를 더할 때만 Date 를 잠깐 쓰고 —
 * 월 경계와 윤년을 손으로 계산할 이유가 없다 — 곧바로 문자열로 되돌린다.
 */

/** 'YYYY-MM-DD' 에 n 일을 더한다. n 이 0 이면 원본을 그대로 돌려준다(참조 유지). */
export function addDaysYmd(dateYmd, days) {
  if (!dateYmd || !days) return dateYmd
  const d = new Date(`${dateYmd}T00:00:00`)
  d.setDate(d.getDate() + days)
  return ymd(d)
}

/** 두 'YYYY-MM-DD' 사이의 일수 (a - b) */
export function diffDaysYmd(a, b) {
  if (!a || !b) return 0
  return Math.round((new Date(`${a}T00:00:00`) - new Date(`${b}T00:00:00`)) / 86400000)
}

/**
 * Date → 서버가 받는 'YYYY-MM-DD HH:mm:ss'.
 *
 * **로컬 시각 그대로** 만든다. toISOString() 을 쓰면 UTC 로 밀려서
 * 오후에 만든 일정이 다음 날로 저장된다(KST 기준 09:00 이전은 전날로).
 * EventDialog 의 폼 값도, 타임그리드 드래그가 만드는 절대 시각도 이 함수 하나로
 * 만든다 — 같은 형식으로 나가야 서버가 두 경로를 구분하지 않는다.
 */
export function toServerDateTime(date) {
  if (!date) return null
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return null
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/**
 * 'YYYY-MM-DD HH:mm:ss' 의 **날짜 부분만** n 일 옮긴다.
 * 시:분:초는 문자열 그대로 보존된다 — 09:00 회의를 사흘 뒤로 끌면
 * 사흘 뒤 09:00 이어야지, 파싱 오차로 08:59 가 되면 안 된다.
 */
export function shiftServerDateTime(value, days) {
  if (!value || !days) return value
  const s = String(value)
  return `${addDaysYmd(s.slice(0, 10), days)}${s.slice(10)}`
}

/**
 * 정규화된 AgendaItem 을 n 일 옮긴 **사본**을 만든다.
 * 원본은 건드리지 않는다 — 드래그 미리보기는 실패하면 되돌려야 하고,
 * 그 되돌리기가 "원본을 그대로 다시 쓰는 것" 이어야 단순하다.
 *
 * 길이(spanDays)와 시각은 유지된다. 이동은 평행이동이지 늘리기가 아니다.
 */
export function shiftAgendaItemDays(item, days) {
  if (!item || !days) return item

  const next = {
    ...item,
    date: addDaysYmd(item.date, days),
    endDate: addDaysYmd(item.endDate, days),
    startAt: shiftServerDateTime(item.startAt, days),
    endAt: shiftServerDateTime(item.endAt, days)
  }
  next.start = parseServerDateTime(next.startAt)
  next.end = parseServerDateTime(next.endAt)

  if (item.kind === 'task') {
    // 업무의 진짜 저장 필드는 startDate/dueDate 다(date/endDate 는 서버가
    // COALESCE 로 합성한 표시용 앵커). 저장 요청도 미리보기도 이 둘을 봐야 한다.
    next.startDate = addDaysYmd(item.startDate, days)
    next.dueDate = addDaysYmd(item.dueDate, days)
    // 지연 배지는 날짜에서 파생된다. 미래로 끌었는데 빨간 테두리가 남아 있으면
    // 미리보기가 결과와 다른 것을 보여주는 셈이다.
    next.isOverdue = !!(next.dueDate && item.status !== 'done' && next.dueDate < ymd(new Date()))
  }

  return next
}
